/*
 * File: AsanaDnd.tsx
 * Project: a-a
 * File Created: Sunday, 11th May 2025 2:43:06 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 24th July 2025 2:45:13 am
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import {
  DndContext,
  DragOverlay,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import zeplinColor from '../../../../theme/Color';
import AsanaTaskCard from '../AsanaTaskCard/AsanaTaskCard';
import { TaskCardUI } from './TaskCardUi';
import { AsanaService } from '../../../../service/asana.service';
import CustomPopOver from '../../../shared/CustomPopOver/CustomPopOver';
import CustomTextField from '../../../shared/CustomTextField/CustomTextField';
import { useInput } from '../../../../customHook/useInput';
import CustomModal from '../../../shared/CustomModal/CustomModal';
import CustomCard from '../../../shared/CustomCard/CustomCard';
import Asana from '../../../../pages/Asana/Asana';
import { useLocation } from 'react-router-dom';

type Response = {
  projectId: string;
  projectName: string;
  updatedDate: string;
  projectLink: string;
  tasksGrouped: { sectionId: string; sectionName: string; tasks: any[] }[];
};

const AsanaDnd = ({
  loading = false,
  data,
  setModalOpen,
  assignee,
  getTaskBySection,
  setDrawerState,
  drawerState,
  filter,
}: {
  loading: boolean;
  data: Response;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  assignee: string[];
  getTaskBySection: (clear?: boolean, deleteTask?: boolean) => Promise<void>;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  filter: boolean;
}) => {
  const location = useLocation();
  const theme = useTheme();
  const sectionInputRef = useRef<HTMLInputElement>(null);
  const selectedAsana = sessionStorage.getItem('selected-task-asana');
  const statusTask = sessionStorage.getItem('status-asana-task');
  const {
    handleChange: handleSectionChange,
    handleBlur: handleSectionBlur,
    clearInput: clearSection,
    value: section,
    invalid: invalidSection,
    didChange: didSectionChange,
  } = useInput('', () => true);
  const [taskGrouped, setTaskGrouped] = useState<Response>(data);
  const [activeId, setActiveId] = useState<string | null>(null); // Track the currently dragged card
  const [draggedCardHeight, setDraggedCardHeight] = useState<number>();
  const [initialDragPosition, setInitialDragPosition] = useState<any>();
  const [aboveCard, setAboveCard] = useState<any>();
  const [belowCard, setBelowCard] = useState<any>();
  const [confirmModal, setConfirmModal] = useState(false);
  const [addSection, setAddSection] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [loadingSection, setLoadingSection] = useState(false);
  const moveTask = async (section: string, task: string) => {
    try {
      const res = await AsanaService.moveTask(section, { taskId: task });
    } catch (err: any) {
      console.log(err);
    }
  };

  const handleDragStart = (event: any) => {
    console.log(`Dragging task: ${event.active?.id}`);
    const draggedElement = document.querySelector(`[data-id="${event.active?.id}"]`);
    if (draggedElement) {
      const rect = draggedElement.getBoundingClientRect();
      setDraggedCardHeight(rect.top); // Set the initial height of the placeholder
      setInitialDragPosition({ x: rect.x, y: rect.y }); // Store the initial position
    }
    setActiveId(event.active?.id); // Store the ID of the dragged card
  };
  const handleDragMove = (event: any) => {
    if (initialDragPosition) {
      const distance = Math.abs(event.delta.y); // Calculate the vertical distance dragged
      const newHeight = Math.max(draggedCardHeight! - distance, 0); // Reduce the height but ensure it doesn't go below 0
      //   setDraggedCardHeight(newHeight); // Update the placeholder height
    }
  };

  const handleDragOver = (event: any) => {
    const { active, over } = event;
    if (over) {
      console.log(`Hovering over: ${over?.id}`);
    }
    // setActiveId(null); // Reset the active card after drop
    const sourceSection = taskGrouped.tasksGrouped.find((section) =>
      section.tasks.some((task) => task?.id === active?.id),
    );
    const targetSection = taskGrouped.tasksGrouped.find(
      (section) => section.sectionId === over?.id,
    );

    if (sourceSection && targetSection) {
      const draggedCardIndex = sourceSection.tasks.findIndex((task) => task.id === active.id);
      const targetIndex = targetSection.tasks.findIndex((task) => task.id === over.id);
      console.log({ draggedCardIndex });
      const aboveCard = targetIndex > 0 ? targetSection.tasks[draggedCardIndex - 1] : null;
      //   const belowCard =
      //     targetIndex < targetSection.tasks.length - 1 ? targetSection.tasks[targetIndex + 1] : null;
      const belowCards = targetSection.tasks.slice(draggedCardIndex); // All cards below the target index

      setAboveCard(aboveCard);
      setBelowCard(belowCards);
      //   console.log('Above Card:', aboveCard?.id);
      //   console.log('Below Card:', belowCard?.id);

      // You can now use this information to adjust the UI dynamically
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setDraggedCardHeight(undefined); // Reset the dragged card height
    setActiveId(null);
    setBelowCard(undefined);
    setAboveCard(undefined);
    if (!over) {
      // If dropped outside any droppable area, reset the activeId and return
      setActiveId(null);
      return;
    }

    const sourceSection = taskGrouped.tasksGrouped.find((section) =>
      section.tasks.some((task) => task?.id === active?.id),
    );
    const targetSection = taskGrouped.tasksGrouped.find(
      (section) => section.sectionId === over?.id,
    );
    if (!targetSection || targetSection === sourceSection) {
      // If no target column is found or the target is the same as the source, do nothing
      // console.log('No valid target column found. Keeping the card in the source column.');
      return;
    }

    if (sourceSection && targetSection) {
      const draggedCardIndex = sourceSection.tasks.findIndex((task) => task?.id === active?.id);
      const draggedCard = sourceSection.tasks[draggedCardIndex];

      if (draggedCard) {
        moveTask(over?.id, active?.id);
        setTaskGrouped((prev) => ({
          ...prev,
          tasksGrouped: prev.tasksGrouped.map((section) => {
            if (section === sourceSection) {
              // Remove the dragged card from the source column
              return {
                ...section,
                tasks: section.tasks.filter((_, index) => index !== draggedCardIndex),
              };
            }
            if (section === targetSection) {
              // Insert the dragged card at the same index in the target column
              const updatedTasks = [...section.tasks];
              updatedTasks.splice(draggedCardIndex, 0, draggedCard);
              return {
                ...section,
                tasks: updatedTasks,
              };
            }
            return section;
          }),
        }));
      }
    }
  };
  const handleCloseConfirm = () => {
    setConfirmModal(false);
    sessionStorage.removeItem('selected-task-asana');
  };
  const handleConfirm = async () => {
    try {
      // setLoading(true);
      const res = await AsanaService.editAsanaTask(selectedAsana || '', {
        completed: statusTask === 'not-completed' ? true : false,
      });
      getTaskBySection();
      handleCloseConfirm();
    } catch (err) {
      console.log(err);
    } finally {
      // setLoading(false);
    }
  };
  useEffect(() => {
    setTaskGrouped(data);
  }, [data]);
  // console.log(activeSectionId, 'activeSectionId', addSection, 'addSection');
  const handleSectionSubmit = async () => {
    try {
      setLoadingSection(true);
      if (section.trim() !== '') {
        await AsanaService.createAsanaSection(location?.pathname?.split('/')[2], {
          name: section,
        });
        clearSection();
        setAddSection(false);
        getTaskBySection();
      }
    } catch (err) {
      clearSection();
      console.log(err);
    } finally {
      setLoadingSection(false);
    }
  };
  const handleSectionEdit = async (value: string, clearInput: () => void) => {
    try {
      setLoadingSection(true);
      if (value.trim() !== '' && activeSectionId) {
        await AsanaService.editAsanaSection(activeSectionId || '', {
          name: value,
        });
        clearInput();
        setActiveSectionId(null);
        getTaskBySection();
      }
    } catch (err) {
      clearInput();
      console.log(err);
    } finally {
      setLoadingSection(false);
    }
  };
  const handleSectionDelete = async (id: string) => {
    try {
      setActiveSectionId(null);
      setConfirmModal(false);
      // setAddSection(false);
      setLoadingSection(true);
      await AsanaService.deleteAsanaSection(id || '');
      getTaskBySection();
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingSection(false);
    }
  };
  useEffect(() => {
    console.log('Focusing on section input', addSection, activeSectionId, sectionInputRef.current);
    if ((addSection || activeSectionId) && addSection) {
      // Add a small delay to ensure the component is rendered
      setTimeout(() => {
        if (sectionInputRef.current) {
          console.log('Focusing on section input after timeout');
          sectionInputRef.current.focus();
        }
      }, 100);
    }
  }, [addSection, activeSectionId]);
  return (
    <Box
      onClick={() => {
        if (addSection) {
          setAddSection(false);
        }
      }}
    >
      <DndContext
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragMove={handleDragMove}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${
              loading
                ? 3
                : taskGrouped?.tasksGrouped?.filter((val) => val?.tasks?.length)?.length <= 0
                ? 1
                : taskGrouped?.tasksGrouped?.length + 1
            }, 1fr)`,
            gap: '16px',
            overflow: 'scroll',
            height: 'calc(100vh - 270px)', // Adjust height to leave space for the UI above
            // paddingBottom: '20rem',
          }}
          onClick={() => {
            if (addSection) {
              setAddSection(false);
            }
          }}
        >
          {loading ? (
            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item, key) => (
              <AsanaTaskCard
                item={{
                  assignee: '',
                  assigneePhoto: '',
                  dueDate: '',
                  id: '',
                  priority: '',
                  priorityColor: '',
                  status: '',
                  statusColor: '',
                  title: '',
                }}
                loading={true}
                key={key}
                id={key.toString()}
              />
            ))
          ) : taskGrouped?.tasksGrouped?.filter((val) => val?.tasks?.length)?.length <= 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '32px',
                flex: 1,
                marginTop: '9rem',
                // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
                // height: '100vh',
                // paddingBottom: '30%',
                // overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '24px',
                  // flex: 1,
                  // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
                }}
              >
                {' '}
                <img src={'/assets/no-tasks.svg'} width={48} height={48} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    // flex: 1,
                    // height: screenHeight > 640 ? '54vh' : screenHeight > 500 ? '41vh' : '39vh',
                  }}
                >
                  <Typography variant='heading3' color={zeplinColor.BasicBlack} sx={{}}>
                    {filter ? 'No Results Found' : 'No Tasks Yet'}
                  </Typography>
                  <Typography
                    variant='body3'
                    color={zeplinColor.Neutral8}
                    sx={{ width: '317px', textAlign: 'center' }}
                  >
                    {filter
                      ? 'Please try adjusting your filters to see relevant results.'
                      : 'Add tasks to organize your project and track progress easily.'}
                  </Typography>
                </Box>
              </Box>
              {filter ? (
                <CustomButton
                  disabled={false}
                  onClick={() => {
                    getTaskBySection(true);
                  }}
                  variant='outlined'
                  color='secondary'
                  style={{
                    width: '150px',
                    borderRadius: '14px',
                    height: '48px',
                    backgroundColor: zeplinColor.Brand1,
                    gap: '16px',
                    padding: '14px 16px',
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={'Clear Filters'}
                />
              ) : (
                <CustomButton
                  disabled={false}
                  variant='outlined'
                  color='secondary'
                  style={{
                    color: zeplinColor.Neutral1,
                    // width: '50%',
                    margin: '10px',
                    padding: '14px 24px',
                    borderRadius: '14px',
                    height: '48px',
                    borderWidth: 1,
                    borderColor: zeplinColor.Neutral1,
                    backgroundColor: zeplinColor.Brand1,
                    display: 'flex',
                    gap: '8px',
                    // '&:hover': { backgroundColor: zeplinColor.Neutral3 },
                  }}
                  text={'Create Task'}
                  icon={<img src={'/assets/add-white.svg'} />}
                  onClick={() => {
                    setModalOpen(true);
                  }}
                  // tabindex={5}
                />
              )}
            </Box>
          ) : (
            taskGrouped?.tasksGrouped.map((section) => (
              <DroppableSection
                key={section.sectionId}
                section={section}
                length={section.tasks.length}
                setModalOpen={setModalOpen}
                activeSectionId={activeSectionId}
                setActiveSectionId={setActiveSectionId}
                setAddSection={setAddSection}
                handleSectionDelete={handleSectionDelete}
                handleSectionEdit={handleSectionEdit}
                loadingSection={loadingSection}
                addSection={addSection}
              >
                {section.tasks.map((task) => (
                  <DraggableTask
                    key={task.id}
                    task={task}
                    loading={loading}
                    activeId={activeId}
                    draggedCardHeight={draggedCardHeight}
                    taskGrouped={taskGrouped}
                    aboveCardId={aboveCard?.id}
                    belowCardId={belowCard}
                    assignee={assignee}
                    getTask={getTaskBySection}
                    setConfirmModal={setConfirmModal}
                    setDrawerState={setDrawerState}
                    drawerState={drawerState}
                  />
                ))}
              </DroppableSection>
            ))
          )}
          {loadingSection ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                width: '100%',
                height: '60px',
              }}
            >
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  height: '20px',
                  // Optionally adjust other styles here
                }}
              />
            </Box>
          ) : (
            addSection && (
              <CustomTextField
                ref={sectionInputRef}
                style={{ width: '100%', marginTop: '10px', paddingTop: '10px' }}
                onBlur={handleSectionBlur}
                onChange={(e) => {
                  handleSectionChange(e);
                }}
                onEnter={(e: any) => {
                  if (e.code === 'Enter') {
                    handleSectionSubmit();
                  }
                }}
                name=''
                text={section}
                error={invalidSection && didSectionChange ? 'Please enter valid Section' : ''}
                label={''}
                placeHolder='Add Section'
                type='text'
                tabindex={1}
                required
              />
            )
          )}
          {!addSection && taskGrouped?.tasksGrouped?.some((val) => val?.tasks?.length) && (
            <CustomButton
              disabled={false}
              variant='outlined'
              color='customSecondary'
              style={{
                color: zeplinColor.BasicBlack,
                // width: '50%',
                margin: '26px',
                padding: '14px 24px',
                borderRadius: '14px',
                height: '48px',
                borderWidth: 1,
                borderColor: zeplinColor.Neutral1,
                display: 'flex',
                gap: '8px',
                '&:hover': { backgroundColor: zeplinColor.Neutral3 },
              }}
              text={'Add Section'}
              icon={<img src={'/assets/add.svg'} />}
              onClick={() => {
                setAddSection(true);
                setActiveSectionId(null); // Reset active section ID when adding a new section
              }}
              // tabindex={5}
            />
          )}
        </Box>
        {/* DragOverlay to render the dragged card */}
        <DragOverlay>
          {activeId ? (
            <Box
              sx={{
                borderRadius: '16px',
                padding: '16px',
                backgroundColor: zeplinColor.Neutral1,
                border: `1px solid ${zeplinColor.BasicBlack}`,
                // width: '195.5px',
                // height: '156px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',

                gap: '24px',
                justifyContent: 'space-between',
                cursor: 'grab',

                transform: 'rotate(-5deg)', // Apply tilt effect
                transition: 'transform 0.2s ease', // Smooth transition
              }}
            >
              <TaskCardUI
                task={taskGrouped.tasksGrouped
                  .flatMap((section) => section.tasks)
                  .find((task) => task.id === activeId)}
                isDragging={false} // Always true for DragOverlay
                loading={false} // Adjust loading state if needed
                setDrawerState={() => {}}
                drawerState={false}
                assigneeTask=''
                getTask={getTaskBySection}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
      <CustomModal handleClose={handleCloseConfirm} open={confirmModal}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CustomCard
            styles={{
              borderRadius: '14px',
              padding: '24px',
              backgroundColor: zeplinColor.Neutral1,

              // width: '312px',
              // height: '201px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '32px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                  Mark as {statusTask === 'not-completed' ? 'Complete' : 'Incomplete'}
                </Typography>
                <Typography variant='body2' color={zeplinColor.BasicBlack}>
                  Are you sure you want to mark this task as <br />
                  {statusTask === 'not-completed' ? 'complete' : 'incomplete'}? You can always undo
                  it later.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',

                  gap: '16px',
                }}
              >
                <Box
                  onClick={handleCloseConfirm}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: zeplinColor.Neutral1,
                      border: `1px solid ${zeplinColor.BasicBlack}`,
                      cursor: 'pointer',
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography variant='buttonMedium' color={zeplinColor.BasicBlack}>
                      Cancel
                    </Typography>
                  </CustomCard>
                </Box>
                <Box
                  onClick={loading ? () => {} : handleConfirm}
                  sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}
                >
                  <CustomCard
                    styles={{
                      borderRadius: '14px',
                      padding: '14px 32px',
                      backgroundColor: loading ? zeplinColor.Neutral8 : zeplinColor.Brand1,
                      cursor: 'pointer',
                      // border: `1px solid ${zeplinColor.BasicBlack}`,
                      // width: '312px',
                      // height: '183px',
                      display: 'flex',
                      justifyContent: 'center',
                      flex: 1,
                      // display: 'flex',
                      // flexDirection: 'column',
                      // justifyContent: 'space-between',
                    }}
                    aria-role='button'
                    aria-disable={loading}
                  >
                    <Typography
                      variant='buttonMedium'
                      color={zeplinColor.Neutral1}
                      sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    >
                      Mark {statusTask === 'not-completed' ? 'Complete' : 'Incomplete'}
                    </Typography>
                  </CustomCard>
                </Box>
              </Box>
            </Box>
          </CustomCard>
          {/* {loading ? <CustomLoader /> : ''} */}
        </Box>
      </CustomModal>
    </Box>
  );
};

export default AsanaDnd;

const DroppableSection = ({
  section,
  children,
  length,
  setModalOpen,
  setActiveSectionId,
  activeSectionId,
  setAddSection,
  handleSectionDelete,
  handleSectionEdit,
  loadingSection,
  addSection,
}: {
  section: any;
  children: React.ReactNode;
  length: number;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveSectionId: React.Dispatch<React.SetStateAction<string | null>>;
  activeSectionId: string | null;
  setAddSection: React.Dispatch<React.SetStateAction<boolean>>;
  handleSectionDelete: (id: string) => Promise<void>;
  handleSectionEdit: (value: string, clearInput: () => void) => Promise<void>;
  loadingSection: boolean;
  addSection: boolean;
}) => {
  const theme = useTheme();
  const sectionInputRef = useRef<HTMLInputElement>(null);

  const {
    handleChange: handleSectionChange,
    handleBlur: handleSectionBlur,
    clearInput: clearSection,
    value: newSection,
    setValue: setNewSection,
    invalid: invalidSection,
    didChange: didSectionChange,
  } = useInput(section.sectionName, () => true);
  console.log(section?.sectionName, 'false', newSection, 'sectionName');
  const { setNodeRef } = useDroppable({ id: section.sectionId });
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  // const [addSection, setAddSection] = useState(false);
  const isEditing = activeSectionId === section.sectionId;
  // console.log({ activeSectionId, sectionId: section.sectionId, isEditing, addSection });
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };
  // Handle section renaming
  const handleRenameClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation(); // Prevent click from propagating to the section container
    // Set this section as the active section being edited
    setActiveSectionId(section.sectionId);
    // If global add section is active, disable it
    setAddSection(false);
    setAnchorEl(null);
  };
  // Handle the "Add section" click in the popover menu
  const handleAddSectionClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation(); // Prevent click from propagating to the section container
    setAddSection(true);
    setActiveSectionId(null);
    setAnchorEl(null);
  };
  // Handle clicks within this section container to prevent propagation
  const handleSectionClick = (e: React.MouseEvent) => {
    setActiveSectionId(null);
  };

  // Handle blur for section renaming
  const handleSectionRenameBlur = (e: React.FocusEvent) => {
    handleSectionBlur();
    // Add your API call to save the renamed section
    // Then clear the active section
    setActiveSectionId(null);
  };
  const [shouldFocus, setShouldFocus] = useState(false);
  useEffect(() => {
    if (activeSectionId === section.sectionId) {
      // Pre-fill the input with current section name
      setNewSection(section.sectionName);

      // Add a small timeout to ensure the input element is rendered
      setTimeout(() => {
        if (sectionInputRef.current) {
          sectionInputRef.current.focus();
        }
      }, 50);
    }
  }, [activeSectionId, section.sectionId, section.sectionName]);
  useEffect(() => {
    if (addSection) {
      setShouldFocus(true);
    } else {
      setShouldFocus(false);
    }
  }, [addSection]);

  useEffect(() => {
    console.log('Focusing on section input', shouldFocus, sectionInputRef.current);
    if (shouldFocus && sectionInputRef.current) {
      sectionInputRef.current.focus();
      setShouldFocus(false);
    }
  }, [shouldFocus]);

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        backgroundColor: zeplinColor.Neutral1,
        borderRadius: '8px',
        // minWidth: '20rem',
        // maxWidth: '30rem',
      }}
      onClick={handleSectionClick}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '56px',
          // minWidth: '20rem',
          // maxWidth: '30rem',
          // marginBottom: '20px',
        }}
      >
        {loadingSection ? (
          <Skeleton
            variant='text'
            sx={{
              fontSize: theme.typography.body2,
              width: '100%',
              // Optionally adjust other styles here
            }}
          />
        ) : isEditing ? (
          <CustomTextField
            ref={sectionInputRef}
            style={{ width: '100%', marginTop: '10px' }}
            onBlur={handleSectionBlur}
            onChange={(e) => {
              handleSectionChange(e);
            }}
            onEnter={(e: any) => {
              console.log('Enter pressed', e);
              if (e.code === 'Enter') {
                handleSectionEdit(newSection, clearSection);
              }
            }}
            name=''
            text={newSection}
            error={invalidSection && didSectionChange ? 'Please enter valid Section' : ''}
            label={''}
            placeHolder='Add Section'
            type='text'
            tabindex={1}
            required
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant='heading3'>
              {section.sectionName} ({length})
            </Typography>
            <img
              width={'18px'}
              height={'18px'}
              src={'/assets/vertical-dots.svg'}
              alt='options-icon'
              onClick={handleClick}
              aria-hidden='false'
              style={{ marginTop: '0.3rem' }}
              // style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
            />{' '}
          </Box>
        )}
      </Box>
      {children}
      <CustomPopOver
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        id={id}
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '16px',
          }}
        >
          {' '}
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={handleRenameClick}
            // onClick={() => {
            //   setAddSection(true);
            //   setAnchorEl(null);
            // }}
          >
            <img src={'/assets/edit.svg'} width={18} height={18} />
            <Typography variant='body3' color={zeplinColor.BasicBlack}>
              Rename section
            </Typography>
          </Box>
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            // onClick={() => handleCopyLink(data?.link)}
            onClick={handleAddSectionClick}
          >
            <img src={'/assets/add.svg'} width={18} height={18} />
            <Typography variant='body3' color={zeplinColor.BasicBlack}>
              Add section
            </Typography>
          </Box>
          <Box
            sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
            onClick={() => {
              setActiveSectionId(null);
              setAnchorEl(null);
              handleSectionDelete(section.sectionId);
            }}
          >
            <img src={'/assets/delete-red.png'} width={18} height={18} />
            <Typography variant='body3' color={zeplinColor.Error}>
              Delete section
            </Typography>
          </Box>
        </Box>
      </CustomPopOver>
      <CustomButton
        disabled={false}
        variant='outlined'
        color='customSecondary'
        style={{
          color: zeplinColor.BasicBlack,
          // width: '50%',
          margin: '10px',
          padding: '14px 24px',
          borderRadius: '14px',
          height: '48px',
          borderWidth: 1,
          borderColor: zeplinColor.Neutral1,
          display: 'flex',
          gap: '8px',
          '&:hover': { backgroundColor: zeplinColor.Neutral3 },
        }}
        text={'Add Task'}
        icon={<img src={'/assets/add.svg'} />}
        onClick={() => {
          setModalOpen(true);
        }}
        // tabindex={5}
      />
    </Box>
  );
};

const DraggableTask = ({
  task,
  loading,
  activeId,
  draggedCardHeight,
  taskGrouped,
  belowCardId,
  aboveCardId,
  assignee,
  getTask,
  setConfirmModal,
  setDrawerState,
  drawerState,
}: {
  task: any;
  loading: boolean;
  activeId: string | null;
  draggedCardHeight: number | undefined;
  taskGrouped: Response;
  belowCardId: any;
  aboveCardId: string | undefined;
  assignee: any[];
  getTask?: (clear?: boolean, deleteTask?: boolean) => Promise<void>;
  setConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
}) => {
  const currentUser = sessionStorage.getItem('current-user');
  const {
    handleChange: handleSearchChange,
    handleBlur: handleSearchBlur,

    value: search,
    invalid: invalidSearch,
    didChange: didSearchChange,
  } = useInput('', () => true);
  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);
  const pointer = useRef<NodeJS.Timeout | null>(null);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: task.id });

  const [timeLimit, setTimeLimit] = useState(false);
  const [assigneeTask, setAssigneeTask] = useState('');

  const [taskAnchorEl, setTaskAnchorEl] = useState<HTMLElement | null>(null);
  const openTask = Boolean(taskAnchorEl);
  const idTask = openTask ? 'simple-popover' : undefined;

  // Find the section containing the dragged card
  const sourceSection = taskGrouped.tasksGrouped.find((section) =>
    section.tasks.some((t) => t.id === activeId),
  );

  // Get the index of the dragged card
  const draggedIndex = sourceSection?.tasks.findIndex((t) => t.id === activeId);

  // Get the index of the current card
  const currentIndex = sourceSection?.tasks.findIndex((t) => t.id === task.id);

  // Determine if this card is below the dragged card
  const isBelowDraggedCard =
    draggedIndex !== undefined && currentIndex !== undefined && currentIndex > draggedIndex;
  // Determine if this card is above or below the placeholder
  const isAbovePlaceholder = task.id === aboveCardId;
  //   const isBelowPlaceholder = task.id === belowCardId;
  const isBelowPlaceholder = belowCardId?.filter((val: any) => val.id === task.id)?.length;
  console.log({ isBelowPlaceholder, belowCardId });
  //   const cardBelow = getCardBelow(taskGrouped, activeId);
  // If this is the dragged card, render a placeholder instead
  if (activeId === task.id) {
    return (
      <Box
        sx={{
          height: '130px', // Dynamically set placeholder height
          backgroundColor: zeplinColor.Neutral3, // Placeholder color
          borderRadius: '8px',

          //   opacity: draggedCardHeight ? 1 : 0, // Fade out as height decreases
        }}
      />
    );
  }

  const handleMouseUp = () => {
    // Clear the timeout if the user releases before the delay
    // console.log(longPressTimeout.current, listeners);
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout?.current);

      longPressTimeout.current = null;

      !timeLimit && !taskAnchorEl && setDrawerState(true);
      !timeLimit && !taskAnchorEl && setAssigneeTask(task?.id);
    }
    pointer?.current && clearTimeout(pointer?.current);
    setTimeLimit(false);
  };
  const handleAssign = async (id: string) => {
    try {
      const res = await AsanaService.editAsanaTask(task?.id, {
        assignee: id,
      });
      getTask && getTask();
      setTaskAnchorEl(null);
    } catch (err: any) {
      console.log(err);
    }
  };
  return (
    <Box
      ref={setNodeRef}
      // {...listeners}

      onPointerDown={(e) => {
        setTimeLimit(false);
        longPressTimeout.current = setTimeout(() => {
          setTimeLimit((prev) => !prev);
        }, 1000); // Long press delay (500ms)
        const target = e.target as HTMLElement;
        if (target.tagName === 'IMG') {
          const altText = (target as HTMLImageElement).alt;
          console.log('Image Alt:', altText);

          // Perform any specific action based on the alt attribute
          if (altText === 'no-assignee-photo') {
            // console.log('Specific image clicked!');
            // setAssigneeTask(task?.id);
            setTaskAnchorEl(target);
          } else if (altText === 'not-completed') {
            setConfirmModal(true);
            sessionStorage.setItem('selected-task-asana', task.id);
            sessionStorage.setItem('status-asana-task', 'not-completed');
          } else if (altText === 'completed') {
            setConfirmModal(true);
            sessionStorage.setItem('selected-task-asana', task.id);
            sessionStorage.setItem('status-asana-task', 'completed');
          }
          return;
        }
        pointer.current = setTimeout(() => listeners?.onPointerDown(e), 1000);
      }}
      {...attributes}
      // onMouseDown={handleMouseDown}
      // onTouchStart={handleMouseDown}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleMouseUp}
      // onClick={handleMouseDown}
      data-id={task.id}
      sx={{
        borderRadius: '16px',

        padding: '16px',
        backgroundColor: isDragging ? zeplinColor.Neutral8 : zeplinColor.Neutral1,
        border: `1px solid ${zeplinColor.Neutral5}`,
        ':hover': {
          ...(!loading && {
            border: `1px solid ${zeplinColor.BasicBlack}`,
          }),
        },
        width: '100%',
        minWidth: '20rem',
        // maxWidth: '30rem',
        // width: '40rem',
        height: '164px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        justifyContent: 'space-between',
        cursor: 'pointer',
        transform: isBelowDraggedCard
          ? `translateY(-${156}px)` // Move up if this card is above the placeholder
          : isBelowPlaceholder
          ? `translateY(${156}px)` // Move down if this card is below the placeholder
          : 'none',
        transition:
          isBelowDraggedCard || isBelowPlaceholder
            ? `transform ${isBelowDraggedCard ? '5s' : '0.5s'} ease`
            : 'none', // Smooth transition
      }}
    >
      <TaskCardUI
        task={task}
        isDragging={isDragging}
        loading={loading}
        setDrawerState={setDrawerState}
        drawerState={drawerState}
        assigneeTask={assigneeTask}
        setAssigneeTask={setAssigneeTask}
        getTask={getTask}
      />
      <CustomPopOver
        setAnchorEl={setTaskAnchorEl}
        anchorEl={taskAnchorEl}
        id={idTask}
        sx={{ overflow: 'hidden' }}
        horizontal='right'
        vertical='top'
      >
        <Box
          sx={{
            borderRadius: '14px',
            boxShadow: 'rgba(0, 0, 0, 0.15)',
            marginTop: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            // gap: '16px',
            // width: '399px',
            height: '350px',
            // backgroundColor: zeplinColor.Neutral1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '16px',
              top: '0px',
              padding: '16px',
              zIndex: 1,
              backgroundColor: zeplinColor.Neutral1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
              }}
            >
              <Typography variant='heading2'>Assignee</Typography>
              <img
                src={'/assets/close-icon.svg'}
                width={24}
                height={24}
                alt='close'
                style={{ cursor: 'pointer' }}
                onClick={() => setTaskAnchorEl(null)}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
              }}
            >
              <CustomTextField
                style={{ width: '100%' }}
                onBlur={handleSearchBlur}
                onChange={handleSearchChange}
                name='Name'
                text={search}
                error={invalidSearch && didSearchChange ? 'Please enter valid Comment' : ''}
                label={'Type name or email'}
                type='text'
              />
              <CustomButton
                disabled={
                  assignee?.filter((val: any) =>
                    val?.name?.toLocaleLowerCase()?.includes(currentUser?.toLocaleLowerCase()),
                  )?.length === 0
                }
                variant='outlined'
                color='customSecondary'
                style={{
                  color: zeplinColor.BasicBlack,
                  width: '50%',
                  margin: 'auto',
                  borderRadius: '14px',
                  height: 56,
                  borderWidth: 1,
                  borderColor: zeplinColor.Neutral9,
                  // display: 'flex',
                  // gap: '16px',
                }}
                text={'Assign to me'}
                // icon={<img src={'/assets/microsoft-logo.svg'} />}
                onClick={() => {
                  const id = assignee?.filter((val: any) =>
                    val?.name?.toLocaleLowerCase()?.includes(currentUser?.toLocaleLowerCase()),
                  )[0]?.id;
                  handleAssign(id);
                }}
                // tabindex={5}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '0px 16px 0px 16px',

              paddingTop: 0,
              marginBottom: '16px',
              overflowY: 'scroll',
              width: '100%',
              // height: '270px',
              // paddingBottom: '5rem',
              // alignSelf: 'stretch',
            }}
          >
            {assignee?.length &&
              assignee
                .filter((val: any) =>
                  val?.name?.toLocaleLowerCase()?.includes(search?.trim()?.toLocaleLowerCase()),
                )
                .map((val: any) => (
                  <Box
                    sx={{
                      display: 'flex',
                      // flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      // alignSelf: 'stretch',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleAssign(val?.id)}
                  >
                    <Box
                      sx={{
                        borderRadius: '100px',
                        // padding: '10px',
                        width: '26px',
                        height: '26px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: task?.assigneePhoto
                          ? zeplinColor.Brand1
                          : zeplinColor.Neutral8,
                      }}
                    >
                      <img
                        src={'/assets/no-assignee.png'}
                        width={'26px'}
                        height={'26px'}
                        style={{ borderRadius: '100px', objectFit: 'cover' }}
                        alt='no-assignee-photo'
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant='body2'>{val?.name}</Typography>
                      <Typography variant='body2'>{val?.name}</Typography>
                    </Box>
                  </Box>
                ))}
          </Box>
        </Box>
      </CustomPopOver>
    </Box>
  );
};
